import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import Layout from "@/components/Layout";
import { api, formatApiError } from "@/lib/apiClient";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { CheckCircle2, Zap } from "lucide-react";

const featured = new Set(["full_bundle", "bundle_plus_guides"]);

const GRANT_LABELS = {
  all_questions: "Tutte le 450 domande con spiegazione (150 verbale · 150 numerica · 150 astratta)",
  guide_verbal: "Guida — Ragionamento Verbale",
  guide_abstract: "Guida — Ragionamento Astratto",
  guide_numerical: "Guida — Ragionamento Numerico",
};
const grantLabel = (g) => GRANT_LABELS[g] || g;
const money = (c) => `€${(c / 100).toFixed(2).replace(".", ",")}`;

export default function Pricing() {
  const [items, setItems] = useState([]);
  const { user } = useAuth();
  const nav = useNavigate();

  useEffect(() => { api.get("/catalog").then((r) => setItems(r.data)); }, []);

  const buy = async (lk, quantity = 1) => {
    if (!user) { toast.info("Accedi per acquistare"); nav("/login"); return; }
    try {
      const { data } = await api.post("/payments/checkout", { lookup_key: lk, origin_url: window.location.origin, quantity });
      window.location.href = data.checkout_url;
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const byKey = (k) => items.find((x) => x.lookup_key === k);
  const owned = (x) => x && user && x.grants.length > 0 && x.grants.every((g) => (user.purchases || []).includes(g));

  return (
    <Layout>
      <div className="max-w-7xl mx-auto px-6 lg:px-10 py-16">
        <div className="label-mono text-[#002FA7] mb-4">— Prezzi</div>
        <h1 className="text-4xl sm:text-5xl lg:text-6xl font-black tracking-tighter mb-4">Paga solo ciò che ti serve.</h1>
        <p className="text-slate-600 max-w-2xl mb-4">Nessun abbonamento. Acquisto una tantum. Le domande e le guide restano nel tuo account.</p>
        <p className="text-slate-600 max-w-2xl mb-8">La banca completa comprende <span className="font-semibold text-[#0A0F1A]">450 domande con spiegazione</span>: 150 di logica verbale, 150 di logica numerica e 150 di logica astratta.</p>
        <div className="border-l-4 border-[#002FA7] bg-slate-50 px-5 py-3 mb-12 text-sm">
          <span className="label-mono text-[#002FA7]">Formula Gratuita</span> — 12 domande per tipo, senza registrazione. <a href="/free" className="underline text-[#002FA7]">Provala subito →</a>
        </div>

        {/* Banca completa */}
        <h2 className="font-display font-bold text-2xl mb-1">La banca completa</h2>
        <p className="text-slate-500 mb-4 text-sm">Sblocca tutte le 450 domande con spiegazione in un'unica formula.</p>
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mb-16 max-w-4xl">
          {["full_bundle", "bundle_plus_guides"].map((k) => { const x = byKey(k); return x ? <Card key={k} item={x} buy={buy} owned={owned(x)} highlight /> : null; })}
        </div>

        {/* Guide */}
        <h2 className="font-display font-bold text-2xl mb-4">Guide con metodo ed esempi</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          {["guide_verbal", "guide_abstract", "guide_numerical", "guides_bundle"].map((k) => { const x = byKey(k); return x ? <Card key={k} item={x} buy={buy} owned={owned(x)} highlight={featured.has(k) || k === "guides_bundle"} /> : null; })}
        </div>
      </div>
    </Layout>
  );
}

function Card({ item, buy, owned, highlight }) {
  return (
    <div className={`border p-6 flex flex-col ${highlight ? "border-[#002FA7] bg-[#0A0F1A] text-white premium-grain" : "border-slate-200 bg-white"}`}>
      {highlight && <div className="label-mono text-[#FFD700] mb-2 flex items-center gap-1"><Zap className="w-3 h-3" />MIGLIOR VALORE</div>}
      <h3 className="font-display font-bold text-lg mb-3 min-h-[3.5rem]">{item.name}</h3>
      <div className={`font-display font-black text-4xl mb-6 ${highlight ? "" : "text-[#002FA7]"}`}>{money(item.amount)}</div>
      <div className="flex-1 space-y-1 text-sm mb-6 opacity-80">
        {item.grants.map((g) => (
          <div key={g} className="flex gap-2"><CheckCircle2 className={`w-4 h-4 ${highlight ? "text-[#FFD700]" : "text-[#002FA7]"}`} />{grantLabel(g)}</div>
        ))}
      </div>
      {owned ? (
        <Button disabled className="w-full rounded-none label-mono py-5 bg-slate-200 text-slate-500">Già in tuo possesso</Button>
      ) : (
        <Button data-testid={`buy-${item.lookup_key}`} onClick={() => buy(item.lookup_key, 1)}
          className={`w-full rounded-none label-mono py-5 ${highlight ? "bg-[#FFD700] hover:bg-white text-[#0A0F1A]" : "bg-[#002FA7] hover:bg-[#001d6b] text-white"}`}>
          Acquista
        </Button>
      )}
    </div>
  );
}
